/** PyTorch XFeat.preprocess_tensor() parity for JS inference.
 *
 * The exported ONNX graph expects spatial dims that are multiples of 32.
 * PyTorch resizes 1200→1184 before the net and maps keypoints back afterward.
 * Without this, JS initial locks drift ~2–3 px vs Python at the same scale.
 *
 * Alternatives (not used here):
 *  - Pad 1200→1216 instead of downscale (different feature distribution)
 *  - Export a fixed-1200 ONNX graph (larger, no runtime resize)
 *  - Skip resize when W/H already divisible by 32 (640px refresh path)
 */
import { detectAndCompute } from "./xfeat.js";

export function floorMul32(n) {
  return Math.max(32, Math.floor(n / 32) * 32);
}

export function netInputSize(w, h) {
  return { netW: floorMul32(w), netH: floorMul32(h) };
}

export function resizeGrayBilinear(gray, w, h, nw, nh) {
  if (w === nw && h === nh) return gray;
  const out = new Float32Array(nw * nh);
  for (let y = 0; y < nh; y++) {
    const sy = Math.max(0, Math.min(h - 1, (y + 0.5) * h / nh - 0.5));
    const y0 = Math.floor(sy), y1 = Math.min(y0 + 1, h - 1), fy = sy - y0;
    for (let x = 0; x < nw; x++) {
      const sx = Math.max(0, Math.min(w - 1, (x + 0.5) * w / nw - 0.5));
      const x0 = Math.floor(sx), x1 = Math.min(x0 + 1, w - 1), fx = sx - x0;
      out[y * nw + x] = gray[y0 * w + x0] * (1 - fx) * (1 - fy)
        + gray[y0 * w + x1] * fx * (1 - fy)
        + gray[y1 * w + x0] * (1 - fx) * fy
        + gray[y1 * w + x1] * fx * fy;
    }
  }
  return out;
}

export function resizeBgr01Bilinear(bgr01, w, h, nw, nh) {
  if (w === nw && h === nh) return bgr01;
  const plane = w * h;
  const out = new Float32Array(3 * nw * nh);
  for (let ch = 0; ch < 3; ch++) {
    const srcOff = ch * plane, dstOff = ch * nw * nh;
    for (let y = 0; y < nh; y++) {
      const sy = Math.max(0, Math.min(h - 1, (y + 0.5) * h / nh - 0.5));
      const y0 = Math.floor(sy), y1 = Math.min(y0 + 1, h - 1), fy = sy - y0;
      for (let x = 0; x < nw; x++) {
        const sx = Math.max(0, Math.min(w - 1, (x + 0.5) * w / nw - 0.5));
        const x0 = Math.floor(sx), x1 = Math.min(x0 + 1, w - 1), fx = sx - x0;
        const i00 = srcOff + y0 * w + x0, i10 = srcOff + y0 * w + x1;
        const i01 = srcOff + y1 * w + x0, i11 = srcOff + y1 * w + x1;
        out[dstOff + y * nw + x] = bgr01[i00] * (1 - fx) * (1 - fy)
          + bgr01[i10] * fx * (1 - fy)
          + bgr01[i01] * (1 - fx) * fy
          + bgr01[i11] * fx * fy;
      }
    }
  }
  return out;
}

export function rescaleKeypoints(det, netW, netH, procW, procH) {
  if (netW === procW && netH === procH) return det;
  const sx = procW / netW, sy = procH / netH;
  for (let i = 0; i < det.n; i++) {
    det.kpts[i * 2] *= sx;
    det.kpts[i * 2 + 1] *= sy;
  }
  return det;
}

/** Grayscale path (browser relock: [1,1,H,W] tensors). */
export function prepareGrayForNet(gray, w, h) {
  const { netW, netH } = netInputSize(w, h);
  const input = (netW === w && netH === h) ? gray : resizeGrayBilinear(gray, w, h, netW, netH);
  return {
    input, netW, netH, procW: w, procH: h,
    rescaleKpts(det) { return rescaleKeypoints(det, netW, netH, w, h); },
  };
}

/** RGB planar BGR01 path (headless learned model: [1,3,H,W]). */
export function prepareBgr01ForNet(bgr01, w, h) {
  const { netW, netH } = netInputSize(w, h);
  const input = (netW === w && netH === h) ? bgr01 : resizeBgr01Bilinear(bgr01, w, h, netW, netH);
  return {
    input, netW, netH, procW: w, procH: h,
    rescaleKpts(det) { return rescaleKeypoints(det, netW, netH, w, h); },
  };
}

/** Run detectAndCompute with py-compat net resize + keypoint rescale. */
export function detectFromNetOutputs(feats, kheat, rel, prepared, topK) {
  const det = detectAndCompute(feats, kheat, rel, prepared.netH, prepared.netW, { topK });
  prepared.rescaleKpts(det);
  return det;
}
